import { createSelector, createSlice } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import { getPostingsAsync, getPostingsByEstateIdAsync } from "./postingListApi";
import type { RootState } from "@/store/store";
import {
  REQUEST_STATUS,
  type RequestStatus,
} from "@/modules/constants/REQUEST_STATUS";
import {
  serializeError,
  type SerializedError,
} from "../../../functions/serializeError";

// Thunks (using createAsyncThunk)

export const fetchPostingsByEstateIdThunk = createAppAsyncThunk(
  "postings/fetchPostingsByEstateId",
  async (estateId: number, { rejectWithValue }) => {
    try {
      const data = await getPostingsByEstateIdAsync(estateId);
      return data;
    } catch (err: any) {
      return rejectWithValue(serializeError(err));
    }
  },
);

// Derive Posting type from API response
type PostingsArray = Awaited<ReturnType<typeof getPostingsAsync>>;
export type Posting = NonNullable<PostingsArray>[number];

type PostingListState = {
  items: Posting[];
  status: RequestStatus;
  error: SerializedError | null;
};

const initialState: PostingListState = {
  items: [],
  status: REQUEST_STATUS.IDLE,
  error: null,
};

const postingListSlice = createSlice({
  name: "postingList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchPostingsByEstateIdThunk
      .addCase(fetchPostingsByEstateIdThunk.pending, (state) => {
        state.status = REQUEST_STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchPostingsByEstateIdThunk.fulfilled, (state, action) => {
        state.status = REQUEST_STATUS.SUCCEEDED;
        state.items = action.payload || [];
      })
      .addCase(fetchPostingsByEstateIdThunk.rejected, (state, action) => {
        state.status = REQUEST_STATUS.FAILED;
        state.error = (action.payload as SerializedError) || {
          message: action.error.message || "Unknown error",
        };
      });
  },
});

export const postingListReducer = postingListSlice.reducer;

// Selector: returns postings as an array for backwards compatibility
const selectPostingList = (state: RootState) => state.postings.postings;
const selectPublishedPostings = createSelector(selectPostingList, (postings) =>
  postings.items.filter((posting) => posting.published),
);

export const postingListSelectors = {
  selectPostingList,
  selectPublishedPostings,
};
